/** Criação de um post da esteira (upload de imagens JPEG). Onda 14. */
import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { PostDTO } from "@unik/shared";
import { api, ApiClientError } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Input, CaptionField } from "../../ui/Input";
import { Select } from "../../ui/Select";

export function PostForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [caption, setCaption] = useState("");
  const [purpose, setPurpose] = useState("aquecer");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const list = Array.from(e.target.files ?? []);
    setFiles(list);
    setPreviews(list.map((f) => URL.createObjectURL(f)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (files.length === 0) {
      setError("Escolha ao menos uma imagem JPEG.");
      return;
    }
    setSaving(true);
    setError(null);
    const form = new FormData();
    form.set("caption", caption);
    form.set("purpose", purpose);
    form.set("scheduledDate", date);
    form.set("scheduledTime", time);
    files.forEach((f) => form.append("media", f));
    try {
      await api.upload<PostDTO>(`/clients/${id}/posts`, form);
      navigate(`/clientes/${id}/esteira`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Não foi possível salvar o post.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell>
      <Link to={`/clientes/${id}/esteira`} className="type-caption text-text-tertiary">
        ← Voltar à esteira
      </Link>
      <h1 className="type-h1 mt-1 text-text-cream">Novo post</h1>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-5" noValidate>
        <div>
          <label className="type-label text-text-secondary">Imagens (JPEG)</label>
          <input
            type="file"
            accept="image/jpeg"
            multiple
            onChange={onFiles}
            className="mt-2 block w-full type-body-sm text-text-secondary file:mr-3 file:cursor-pointer file:rounded-[var(--radius-control)] file:border-0 file:bg-surface-brand file:px-4 file:py-2 file:text-text-on-brand"
          />
          {previews.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {previews.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="h-20 w-20 shrink-0 rounded-[var(--radius-control)] object-cover"
                />
              ))}
            </div>
          )}
          <p className="type-caption mt-2 text-text-tertiary">
            Uma imagem para post único, várias para carrossel. Só JPEG (exigência do Instagram).
          </p>
        </div>

        <CaptionField label="Legenda" value={caption} onChange={(e) => setCaption(e.target.value)} />

        <Select label="Função" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
          <option value="aquecer">Aquecer</option>
          <option value="qualificar">Qualificar</option>
          <option value="vender">Vender</option>
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Input label="Horário" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>

        {error !== null && (
          <p className="type-caption text-danger" role="alert">{error}</p>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={saving}>Salvar post</Button>
          <Button type="button" variant="ghost" onClick={() => navigate(`/clientes/${id}/esteira`)}>
            Cancelar
          </Button>
        </div>
      </form>
    </AdminShell>
  );
}
